import { useAuthStore } from '@/common/stores/auth';
import { assetUrl } from '@/helpers/assetUrl';
import { axios } from '@/helpers/axios';
import { usePaddleStore } from '@/modules/pricing/stores/paddle';

const generateAffiliateMetaInfo = (affiliate: string) => {
  const titles = {
    jaysway: "eRank — Claim your free trial from Jay's Way",
    erank: 'eRank — Claim your 7-day free trial today',
    goldcityventures: 'eRank Plans — Claim your 7-Day Free Trial today',
    printify: 'eRank — Claim your free trial from Printify',
    haa: 'eRank — Claim your free trial from Handmade Alpha Academy',
    rhymeexport: 'eRank Plans — Claim your Discount today',
    ecomhannah: 'eRank Plans — Get 14 Days Free of eRank + ProfitTree Bonuses!',
    moneywithmak: 'eRank Plans — Claim your 14-Day Free Trial today',
    mayfli: 'eRank Plans — Claim your 6-month free trial of eRank PRO',
    alisonprince: 'eRank Plans — Claim your 60-Day Free Trial today',
  };

  return {
    title: titles[affiliate] || 'eRank Plans — Claim your 7-Day Free Trial today',
    metaTags: [
      {
        name: 'description',
        content: 'Start your 7-Day Free Trial to try out the full version of eRank for FREE.',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0',
      },
      {
        'http-equiv': 'X-UA-Compatible',
        content: 'ie=edge',
      },
      {
        'data-vue-meta': '1',
        vmid: 'description',
        name: 'description',
        content: 'eRank has three plans, great for beginners, intermediate, and advanced sellers on Etsy.',
      },
      {
        'data-vue-meta': '1',
        vmid: 'og:title',
        property: 'og:title',
        content: 'eRank Plans — Find the best plan for your Etsy shop',
      },
      {
        'data-vue-meta': '1',
        vmid: 'og:description',
        property: 'og:description',
        content: 'Start your 7-Day Free Trial to try out the full version of eRank for FREE.',
      },
      {
        'data-vue-meta': '1',
        vmid: 'og:site_name',
        property: 'og:site_name',
        content: 'eRank LTD',
      },
      {
        'data-vue-meta': '1',
        vmid: 'og:type',
        property: 'og:type',
        content: 'website',
      },
      {
        'data-vue-meta': '1',
        vmid: 'og:image',
        property: 'og:image',
        content: assetUrl('/img/erank-meta.png'),
      },
      {
        name: 'twitter:title',
        content: 'eRank Plans — Find the best plan for your Etsy shop',
        'data-vmid': 'twitter:title',
      },
      {
        name: 'twitter:description',
        content: 'Start your 7-Day Free Trial to try out the full version of eRank for FREE.',
        'data-vmid': 'twitter:description',
      },
      {
        name: 'twitter:image',
        content: assetUrl('/img/erank-meta.png'),
        'data-vmid': 'twitter:image',
      },
      {
        name: 'twitter:card',
        content: 'summary',
        vmid: 'twitter:card',
      },
      {
        name: 'p:domain_verify',
        content: '4a452b9edfa7318bd9a45fe0d20a25c0',
      },
      {
        name: 'google-site-verification',
        content: '-2cfMO7MeRJeifFFxsIOq3Ifssa2ai61ngBq40DYYkM',
      },
      {
        name: 'msvalidate.01',
        content: '65CFA6CFB407B1A92CCA3F3FB7B1D174',
      },
      {
        'http-equiv': 'Content-Type',
        content: 'text/html; charset= ISO-8859-1',
      },
    ],
  };
};

export default [
  {
    path: '/b/:affiliate',
    name: 'affiliate_plans_redesign',
    component: () => import('@/modules/pricing-affiliate-redesign/pages/Entry.vue'),
    props: true,
    meta: (route) => generateAffiliateMetaInfo(route.params.affiliate),
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      const paddleStore = usePaddleStore();
      const link = await paddleStore.checkPaddleRestriction();
      const isUsingPayPal = authStore.user?.subscription_gateway === 'paypal' || authStore.user?.subscription_gateway === 'stripe';
      
      const referrer = authStore.user?.referrer;
      const isFreeMember = authStore.user?.subscription_period === '';
      const hasNeverHadTrial = !authStore.user?.trial_start_date;
      const hasNeverBeenPaid = !authStore.user?.subscription_id;
      const hasActiveTrial = authStore.user?.trial_start_date && !authStore.user?.trial_end_date;

      // Archived affiliates that should redirect to /plans
      const archivedAffiliates = ['rhymeexport', 'alisonprince', 'mayfli'];
      if (archivedAffiliates.includes(to.params.affiliate)) {
        return next('/plans');
      }

      const validAffiliates = [
        'ad',
        'ecomhannah',
        'erank',
        'goldcityventures',
        'haa',
        'jaysway',
        'moneywithmak',
        'printify',
      ];

      // Fetch and set affiliate-specific Paddle plans to ensure correct pricing
      const affiliate = to.params.affiliate as string;
      if (validAffiliates.includes(affiliate)) {
        try {
          const response = await axios.get(`/affiliate-plans/${affiliate}`);
          if (response.data) {
            window.__PLANS__.product_prices = response.data;
          }
        } catch (error) {
          console.error('Failed to fetch affiliate plans:', error);
          // Continue with existing plans if fetch fails
        }
      }

      // Check Paddle restrictions first

      if (isUsingPayPal || link) {
        window.location.href = `${import.meta.env.VITE_ERANK_V2_URL}/plans`; // TODO: change to /plans
        return;
      }

      // Allow access to affiliate pages for free members who haven't had a trial or been paid members
      if (isFreeMember && hasNeverHadTrial && hasNeverBeenPaid) {
        if (validAffiliates.includes(to.params.affiliate)) {
          next();
        } else {
          next('/plans');
        }
        return;
      }

      // If user has an active trial or has completed a trial, redirect to plans
      if (hasActiveTrial || !hasNeverHadTrial) {
        next('/plans');
        return;
      }

      // If trying to access a non-erank affiliate when referrer is 'ad', redirect to erank
      if (referrer === 'ad' && to.params.affiliate !== 'erank') {
        next('/b/erank');
        return;
      }

      // If trying to access a different affiliate than referrer, handle based on redirectToReferralPage
      if (to.params.affiliate !== referrer && referrer && to.params.affiliate !== 'erank') {
        if (authStore.redirectToReferralPage) {
          next('/b/' + referrer);
        } else {
          next('/plans');
        }
        return;
      }

      // If no referrer, allow access to erank route, otherwise redirect to plans
      if (!referrer && to.params.affiliate !== 'erank') {
        next('/plans');
        return;
      }

      // Validate affiliate
      if (validAffiliates.includes(to.params.affiliate)) {
        next();
      } else {
        next('/plans');
      }
    },
  },
];
